import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { PageContainer } from '../../components/layout';
import { Tabs, TabPane, ConfirmModal } from '../../components/ui';
import type { TabDef } from '../../components/ui';
import { adminClientsService, ClientDetail } from '../../services/adminClientsService';
import { IdentityTab, type AutonomousTabHandle } from '../../components/parametres/clients/IdentityTab';
import { StorageTab } from '../../components/parametres/clients/StorageTab';
import { IngestionTab } from '../../components/parametres/clients/IngestionTab';
import { ExtractionTab } from '../../components/parametres/clients/ExtractionTab';
import { DomainsConfigsTab } from '../../components/parametres/clients/DomainsConfigsTab';
import { UsersTab } from '../../components/parametres/clients/UsersTab';
import { IntegrationsTab } from '../../components/parametres/clients/IntegrationsTab';

const TABS: TabDef[] = [
  { key: 'identity', label: 'Identité' },
  { key: 'storage', label: 'Storage' },
  { key: 'ingestion', label: 'Ingestion' },
  { key: 'extraction', label: 'Extraction' },
  { key: 'domains-configs', label: 'Domaines & Configs' },
  { key: 'users', label: 'Utilisateurs' },
  { key: 'integrations', label: 'Intégrations' },
];

const VALID_KEYS = new Set(TABS.map((t) => t.key));

export const ClientEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tabFromUrl = searchParams.get('tab') || 'identity';
  const activeTab = VALID_KEYS.has(tabFromUrl) ? tabFromUrl : 'identity';

  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const identityRef = useRef<AutonomousTabHandle>(null);
  const storageRef = useRef<AutonomousTabHandle>(null);
  const ingestionRef = useRef<AutonomousTabHandle>(null);
  const extractionRef = useRef<AutonomousTabHandle>(null);
  const integrationsRef = useRef<AutonomousTabHandle>(null);

  const getCurrentTabRef = (): React.RefObject<AutonomousTabHandle> | null => {
    switch (activeTab) {
      case 'identity':
        return identityRef;
      case 'storage':
        return storageRef;
      case 'ingestion':
        return ingestionRef;
      case 'extraction':
        return extractionRef;
      case 'integrations':
        return integrationsRef;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminClientsService.getClient(id);
        if (cancelled) return;
        setClient(data);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleTabChange = (next: string) => {
    if (next === activeTab) return;
    const ref = getCurrentTabRef();
    if (ref?.current?.getIsDirty()) {
      setPendingTab(next);
      return;
    }
    setSearchParams({ tab: next }, { replace: true });
  };

  const confirmDiscardAndSwitch = () => {
    if (pendingTab) {
      const ref = getCurrentTabRef();
      ref?.current?.reset();
      setSearchParams({ tab: pendingTab }, { replace: true });
    }
    setPendingTab(null);
  };

  const cancelDiscard = () => setPendingTab(null);

  const handleSaved = (updated: ClientDetail) => {
    setClient(updated);
  };

  if (loading) {
    return <PageContainer><div className="text-gray-500 py-8 text-center">Chargement...</div></PageContainer>;
  }

  if (error || !client) {
    return (
      <PageContainer>
        <div className="text-red-600 py-8 text-center">{error || 'Client introuvable'}</div>
        <div className="text-center">
          <button onClick={() => navigate('/parametres/clients')} className="text-blue-600 hover:underline text-sm">
            ← Retour à la liste
          </button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="mb-4 text-sm text-gray-600">
        <Link to="/parametres/clients" className="hover:underline">Clients</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 font-medium">{client.name}</span>
      </div>

      <h1 className="text-2xl font-bold mb-6">{client.name}</h1>

      <Tabs tabs={TABS} activeKey={activeTab} onChange={handleTabChange}>
        {activeTab === 'identity' && (
          <TabPane tabKey="identity">
            <IdentityTab
              ref={identityRef}
              clientId={id!}
              initialData={client}
              onSaved={handleSaved}
            />
          </TabPane>
        )}
        {activeTab === 'storage' && (
          <TabPane tabKey="storage">
            <StorageTab
              ref={storageRef}
              clientId={id!}
              initialData={client}
              onSaved={handleSaved}
            />
          </TabPane>
        )}
        {activeTab === 'ingestion' && (
          <TabPane tabKey="ingestion">
            <IngestionTab
              ref={ingestionRef}
              clientId={id!}
              initialData={client}
              onSaved={handleSaved}
            />
          </TabPane>
        )}
        {activeTab === 'extraction' && (
          <TabPane tabKey="extraction">
            <ExtractionTab
              ref={extractionRef}
              clientId={id!}
              initialData={client}
              onSaved={handleSaved}
            />
          </TabPane>
        )}
        {activeTab === 'domains-configs' && (
          <TabPane tabKey="domains-configs">
            <DomainsConfigsTab clientId={id!} isSystem={client.is_system_tenant === true} />
          </TabPane>
        )}
        {activeTab === 'users' && (
          <TabPane tabKey="users">
            <UsersTab clientId={id!} isSystem={client.is_system_tenant === true} />
          </TabPane>
        )}
        {activeTab === 'integrations' && (
          <TabPane tabKey="integrations">
            <IntegrationsTab ref={integrationsRef} clientId={id!} isSystem={client.is_system_tenant === true} />
          </TabPane>
        )}
      </Tabs>

      <ConfirmModal
        open={pendingTab !== null}
        title="Modifications non sauvegardées"
        message="Vous avez des modifications non enregistrées. Les abandonner et changer d'onglet ?"
        confirmLabel="Abandonner"
        cancelLabel="Rester"
        variant="danger"
        onConfirm={confirmDiscardAndSwitch}
        onCancel={cancelDiscard}
      />
    </PageContainer>
  );
};

export default ClientEditPage;
